import { AudioPreview } from 'mytours-read-common'
import { AudioUrlFile, MediaItem } from 'mytours-read-api-typescript'

import { BaseUrlEnum, fetchAudio, generateFilePath } from '../../common_lib/utils'

export interface AudioSource {
	src: string
	format: string
	// Absent for URL-sourced audio, which has no uploaded file behind it. It is half the key every
	// soundController lookup is made on, so callers must pass it through as-is rather than defaulting it.
	fileId?: number
}

/**
 * Where a media item's audio actually lives, and what format Howler should expect. Two shapes:
 * an external URL the author supplied, or an upload served from Bunny.
 */
export const resolveAudioSource = async (audio: AudioPreview): Promise<AudioSource | undefined> => {
	const { file, source, dont_transform: dontTransform } = audio

	if (!file) {
		return undefined
	}

	// MediaItem.file is `any` because the generator collapses one_of [FileRec, AudioUrlFile] — narrow via
	// the discriminator rather than by probing for media_url, which an uploaded file could grow.
	const externalUrl = source === MediaItem.SourceEnum.Url ? (file as AudioUrlFile).media_url : undefined

	if (externalUrl) {
		const pathExtension = externalUrl.split('?')[0].split('.').pop()?.toLowerCase()

		return { src: externalUrl, format: pathExtension || 'mp3' }
	}

	const { id: fileId, filename, region } = file
	const { base, path } = generateFilePath(String(fileId), BaseUrlEnum.BunnyUploads, {
		pathType: dontTransform ? 'original' : 'quality_7',
		region,
	})

	let bunnySrc = base + path + filename

	// A transformed upload is served as mp3 when one exists, so prefer it; the HEAD probe is what tells
	// us whether it does. An untransformed one is served in whatever format it was uploaded in.
	if (!dontTransform) {
		const probed = await fetchAudio(bunnySrc)
		if (probed) {
			bunnySrc = probed
		}
	}

	return {
		src: bunnySrc + '?method=howl',
		format: dontTransform ? filename.split('.').reverse()[0] : 'mp3',
		fileId,
	}
}
